import { useEffect, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_SLIDES } from '@/lib/constants';

export default function HeroCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', skipSnaps: false },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  return (
    <div
      className="relative rounded-xl overflow-hidden border bg-card shadow-[var(--shadow-card)]"
      data-testid="hero-carousel"
    >
      {/* Embla viewport */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {HERO_SLIDES.map((slide, i) => (
            <div
              key={slide.src}
              className="relative shrink-0 grow-0 basis-full"
              data-testid={`hero-slide-${i}`}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                title={slide.caption}
                className="block w-full h-[320px] sm:h-[380px] lg:h-[440px] object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : 'auto'}
              />
              {/* Caption gradient + text */}
              <div className="absolute inset-x-0 bottom-0 pointer-events-none bg-gradient-to-t from-black/55 via-black/20 to-transparent">
                <div className="px-4 sm:px-5 py-3 sm:py-4">
                  <p className="text-white text-xs sm:text-sm font-medium tracking-wide drop-shadow">
                    {slide.caption}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prev / Next controls */}
      <button
        type="button"
        onClick={scrollPrev}
        aria-label="Previous photo"
        data-testid="hero-carousel-prev"
        className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10 inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-background/85 hover:bg-background backdrop-blur border border-border/70 text-foreground transition-[transform,background-color] hover:scale-105 active:scale-95"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={scrollNext}
        aria-label="Next photo"
        data-testid="hero-carousel-next"
        className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10 inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-background/85 hover:bg-background backdrop-blur border border-border/70 text-foreground transition-[transform,background-color] hover:scale-105 active:scale-95"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dot indicators */}
      <div
        className="absolute inset-x-0 bottom-2.5 sm:bottom-3 flex items-center justify-center gap-1.5 z-10"
        role="tablist"
        aria-label="Choose photo"
      >
        {HERO_SLIDES.map((slide, i) => {
          const active = i === selectedIndex;
          return (
            <button
              key={slide.src}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={`Show photo ${i + 1}`}
              onClick={() => scrollTo(i)}
              data-testid={`hero-carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-[width,background-color,opacity] duration-300 ${
                active
                  ? 'w-6 bg-white shadow-sm'
                  : 'w-1.5 bg-white/70 hover:bg-white opacity-90'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
}
